import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { API, getClientId } from "@/lib/saathiClient";

// Hands-free voice loop: detect speech → record → /voice/turn → play reply → listen again. Speaking over Saathi interrupts it.
const START_LEVEL = 0.035;
const BARGE_LEVEL = 0.07;
const START_MS = 180;
const SILENCE_MS = 900;
const MAX_UTTER_MS = 30000;

export function useVoiceLoop() {
  const [status, setStatus] = useState("idle"); // idle | connecting | live | ended | error
  const [phase, setPhase] = useState("listening"); // listening | user | thinking | saathi
  const [error, setError] = useState("");
  const [turns, setTurns] = useState([]);
  const [muted, setMuted] = useState(false);
  const R = useRef({});

  const setPh = (p) => { R.current.phase = p; setPhase(p); };

  const addTurn = (role, content) => {
    R.current.turns = [...(R.current.turns || []), { role, content }];
    setTurns(R.current.turns);
  };

  const stopPlayback = () => {
    const a = R.current.player;
    if (a) { a.pause(); a.src = ""; R.current.player = null; }
  };

  const play = useCallback((src) => new Promise((resolve) => {
    stopPlayback();
    const a = new Audio(src);
    R.current.player = a;
    setPh("saathi");
    a.onended = a.onerror = () => { if (R.current.player === a) { R.current.player = null; setPh("listening"); } resolve(); };
    a.play().catch(() => { R.current.player = null; setPh("listening"); resolve(); });
  }), []);

  const sendTurn = useCallback(async (blob) => {
    const c = R.current;
    const req = ++c.reqId;
    setPh("thinking");
    const fd = new FormData();
    fd.append("audio", blob, "turn.webm");
    fd.append("language_code", c.lang.code);
    fd.append("language_name", c.lang.sub);
    fd.append("history", JSON.stringify([...(c.history || []), ...(c.turns || [])]));
    if (c.conversationId) fd.append("conversation_id", c.conversationId);
    if (c.orgSlug) fd.append("org_slug", c.orgSlug);
    fd.append("client_id", getClientId());
    try {
      const { data } = await axios.post(`${API}/voice/turn`, fd);
      if (req !== c.reqId || !c.active) return;
      c.conversationId = data.conversation_id;
      addTurn("user", data.transcript);
      addTurn("assistant", data.reply);
      await play(`data:audio/mpeg;base64,${data.audio_b64}`);
    } catch (e) {
      if (req !== c.reqId || !c.active) return;
      const d = e?.response?.data?.detail;
      if (d === "daily_limit" || d === "voice_unavailable") { setError(d === "daily_limit" ? "daily_limit" : "unavailable"); setStatus("error"); return; }
      setPh("listening");
    }
  }, [play]);

  const beginRecording = useCallback(() => {
    const c = R.current;
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "";
    const rec = new MediaRecorder(c.stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    rec.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
    rec.onstop = () => {
      const dur = Date.now() - c.recStart;
      if (!c.active || dur < 500) { if (c.active) setPh("listening"); return; }
      sendTurn(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
    };
    c.rec = rec;
    c.recStart = Date.now();
    rec.start();
    setPh("user");
  }, [sendTurn]);

  const tick = useCallback(() => {
    const c = R.current;
    if (!c.active) return;
    c.analyser.getFloatTimeDomainData(c.buf);
    let sum = 0;
    for (let i = 0; i < c.buf.length; i++) sum += c.buf[i] * c.buf[i];
    const level = c.muted ? 0 : Math.sqrt(sum / c.buf.length);
    const now = Date.now();
    const ph = c.phase;
    const threshold = ph === "saathi" ? BARGE_LEVEL : START_LEVEL;
    if (ph === "listening" || ph === "saathi") {
      if (level > threshold) {
        c.loudSince = c.loudSince || now;
        if (now - c.loudSince > START_MS) {
          c.loudSince = 0;
          if (ph === "saathi") stopPlayback();
          c.quietSince = 0;
          beginRecording();
        }
      } else c.loudSince = 0;
    } else if (ph === "user") {
      if (level < START_LEVEL) {
        c.quietSince = c.quietSince || now;
        if (now - c.quietSince > SILENCE_MS) c.rec?.state === "recording" && c.rec.stop();
      } else c.quietSince = 0;
      if (now - c.recStart > MAX_UTTER_MS) c.rec?.state === "recording" && c.rec.stop();
    }
    c.raf = requestAnimationFrame(tick);
  }, [beginRecording]);

  const cleanup = useCallback(() => {
    const c = R.current;
    c.active = false;
    cancelAnimationFrame(c.raf);
    stopPlayback();
    try { if (c.rec?.state === "recording") c.rec.stop(); } catch { /* noop */ }
    c.stream?.getTracks().forEach((t) => t.stop());
    c.ctx?.close().catch(() => {});
    Object.assign(c, { stream: null, ctx: null, rec: null });
  }, []);

  const start = useCallback(async ({ lang, orgSlug, history, conversationId, greeting }) => {
    Object.assign(R.current, { lang, orgSlug, history, conversationId, turns: [], reqId: 0, phase: "listening", muted: false });
    setTurns([]); setError(""); setMuted(false); setStatus("connecting");
    try {
      R.current.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch {
      setStatus("error"); setError("mic");
      return;
    }
    const c = R.current;
    c.ctx = new (window.AudioContext || window.webkitAudioContext)();
    const src = c.ctx.createMediaStreamSource(c.stream);
    c.analyser = c.ctx.createAnalyser();
    c.analyser.fftSize = 1024;
    c.buf = new Float32Array(c.analyser.fftSize);
    src.connect(c.analyser);
    c.active = true;
    setStatus("live");
    c.raf = requestAnimationFrame(tick);
    if (greeting) {
      try {
        const res = await axios.post(`${API}/voice/tts`, { text: greeting, language_code: lang.code }, { responseType: "blob" });
        if (c.active && c.phase === "listening") await play(URL.createObjectURL(res.data));
      } catch { /* greeting audio is optional */ }
    }
  }, [tick, play]);

  const end = useCallback(() => {
    cleanup();
    setStatus("ended");
    return { turns: R.current.turns || [], conversationId: R.current.conversationId || null };
  }, [cleanup]);

  const toggleMute = useCallback(() => {
    R.current.muted = !R.current.muted;
    setMuted(R.current.muted);
  }, []);

  return { status, phase, error, turns, muted, start, end, toggleMute };
}
